/**
 * Correlation identifiers for bug-report triage (S-2 follow-on).
 *
 * Two keys, two lifetimes:
 *  - sessionId — one per browser tab session (sessionStorage), survives
 *    navigations within the tab. Attached to bug-report context AND tagged on
 *    every AppSignal error report, so a ticket can be matched to the
 *    incidents its session produced.
 *  - traceId — one per page load (module-level), regenerated on full reload.
 *    Tighter correlation window than sessionId; also tagged on both sides.
 *
 * Both are safe to call on the server (return undefined / a stable no-op) —
 * exception-handler.ts imports this from universal code.
 */

const STORAGE_KEY = 'wellmate.sessionId';

// Safari private mode throws on sessionStorage.setItem; fall back to memory
// so the id is still stable within the page's lifetime.
let inMemorySessionId: string | undefined;

function randomUuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // Insecure context fallback — good enough for correlation.
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export function getOrCreateSessionId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const existing = window.sessionStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const id = randomUuid();
    window.sessionStorage.setItem(STORAGE_KEY, id);
    return id;
  } catch {
    inMemorySessionId ??= randomUuid();
    return inMemorySessionId;
  }
}

// One per page load. Module scope means every importer within the same
// bundle/page-load sees the same value.
let pageLoadTraceId: string | undefined;

export function getPageLoadTraceId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  pageLoadTraceId ??= randomUuid();
  return pageLoadTraceId;
}
